import React, { useState } from 'react';
import { Coins } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useUnits, CURRENCIES } from '../../context/UnitsContext';
import { useAuth } from '../../context/AuthContext';
import { preferencesAPI } from '../../lib/api';
import { useI18n } from '../../i18n';
import { cn } from '../../lib/utils';

const CURRENCY_OPTIONS = [
  { value: CURRENCIES.USD, labelKey: 'settings.currencyUSD' },
  { value: CURRENCIES.AUD, labelKey: 'settings.currencyAUD' },
  { value: CURRENCIES.COP, labelKey: 'settings.currencyCOP' },
  { value: CURRENCIES.EUR, labelKey: 'settings.currencyEUR' },
  { value: CURRENCIES.CAD, labelKey: 'settings.currencyCAD' },
];

/**
 * Display currency picker — updates global UnitsContext and persists to user preferences when logged in.
 */
export function DisplayCurrencySelect({
  className = '',
  testId = 'display-currency-select',
  variant = 'default',
  compact = false,
  shortLabels = false,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { currency, setCurrency } = useUnits();
  const [saving, setSaving] = useState(false);

  const handleChange = async (next) => {
    if (!Object.values(CURRENCIES).includes(next)) return;
    setCurrency(next);
    if (!user) return;
    setSaving(true);
    try {
      await preferencesAPI.update({ currency: next });
    } catch (err) {
      console.error('Failed to save currency preference:', err);
    } finally {
      setSaving(false);
    }
  };

  const isSidebar = variant === 'sidebar';
  const useShort = shortLabels || isSidebar;
  const currentLabel = CURRENCY_OPTIONS.find((o) => o.value === currency);

  return (
    <Select value={currency} onValueChange={handleChange} disabled={saving}>
      <SelectTrigger
        className={cn(
          compact ? 'h-9 min-w-9 px-1 justify-center shrink-0' : 'min-w-0 shrink-0',
          !compact && !isSidebar && 'w-[9.5rem] sm:w-44',
          !compact && isSidebar && 'w-[4.5rem] px-2',
          !compact && !isSidebar && 'w-full',
          isSidebar &&
            'bg-[#0A2F52] border-[#0A2F52] text-slate-200 hover:bg-[#0D3E6B] hover:text-white',
          className,
        )}
        data-testid={testId}
        title={currentLabel ? (useShort ? currency : t(currentLabel.labelKey)) : undefined}
      >
        <div className={cn('flex items-center justify-center gap-1 min-w-0', compact && 'w-full')}>
          {!useShort && !compact && (
            <Coins className={cn('h-4 w-4 shrink-0', isSidebar ? 'text-slate-400' : 'text-slate-500')} />
          )}
          {useShort || compact ? (
            <span className="font-semibold text-xs tabular-nums leading-none">{currency}</span>
          ) : (
            <SelectValue placeholder={t('settings.selectCurrency')} />
          )}
        </div>
      </SelectTrigger>
      <SelectContent>
        {CURRENCY_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {useShort ? opt.value : t(opt.labelKey)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default DisplayCurrencySelect;
